import { Component, inject, Input, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup } from '@angular/forms';
import { extractErrorList, getFormData } from 'src/app/shared/helpers/utils';
import { TransactionFormProvider } from '../transaction-form-provider';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpService } from 'src/app/services/http.service';
import { ORIGINATOR_ID } from 'src/app/shared/enums/common.enum';
import Swal from 'sweetalert2';
import * as moment from 'moment';
import { COL_DEFS } from './constants/table.columns.const';
import { ColDef, GridReadyEvent } from '@ag-grid-community/core';
import {
  CCY_SORT_ORDER,
  Transaction_Types,
} from 'src/app/shared/constants/helpers.const';
import { CommonService } from 'src/app/services/common.service';
import { Subscription } from 'rxjs';
import { Reference_Data } from '../services/transaction-update-reference.data';
import { CashReconcilationComponent } from '../cash-reconcilation/cash-reconcilation.component';
import { TradeHttpService } from '../services/trade-http.service';
import { SummaryByOriginatorResponseInterface } from 'src/app/shared/interfaces/summary-by-originator-response.interface';
import { selectCurrencyList } from '../../shared/state/selectors/currency.selector';
import { Store } from '@ngrx/store';
import { SpinnerService } from "../../shared/services/spinner.service";

@Component({
  selector: 'app-transaction-update',
  templateUrl: './transaction-update.component.html',
  styleUrls: ['./transaction-update.component.scss'],
})
export class TransactionUpdateComponent implements OnInit, OnDestroy {
  colDefs: ColDef[] = COL_DEFS(this);
  rowData: any[] = [];
  transactionUpdateForm: FormGroup;
  @Input() transactionType: string;
  @Input() pageTitle = '';
  gridApi: any;
  params: any;
  currencyList: string[] = [];
  ccySortOrder = CCY_SORT_ORDER;
  private subscriptions: Subscription = new Subscription();
  isSubmitted = false;
  transactionTypes = Transaction_Types;
  balance = 0;
  disableFileChange = false;
  credebtors: { id: string; name: string }[] = [];
  debtors: { id: string; name: string }[] = [];
  originatorId = ORIGINATOR_ID;
  maxDate: Date;
  transactionTypeToTypeMap = new Map<string, string>([
    ['Credit Note', 'Credit Note'],
    ['Bank Transmission Receipt', 'Receipt'],
    ['Notice of Debtor Remittance', 'Remittance'],
    // ['Receipt', 'Bank Transmission Receipt'],
    // ['Remittance', 'Notice of Debtor Remittance'],
  ]);
  #spinnerService = inject(SpinnerService);

  constructor(
    private formProvider: TransactionFormProvider,
    public activeModal: NgbActiveModal,
    private http: HttpService,
    private commonService: CommonService,
    private modalService: NgbModal,
    private tradeTransactionsHttpService: TradeHttpService,
    private store: Store,
  ) {}

  get typeCtrl(): FormControl {
    return this.transactionUpdateForm.get('transactionType') as FormControl;
  }

  get fileCtrl(): FormControl {
    return this.transactionUpdateForm.get('file') as FormControl;
  }

  ngOnInit(): void {
    this.transactionUpdateForm = this.formProvider.initForm();
    if (this.transactionType && this.transactionType !== this.typeCtrl?.value) {
      this.updateTransactionType();
    }
    this.subscribeToTransactionChange();
    this.getCredebtorOverviewList();
    this.getDebtorsList();
    this.getCcyList();
    this.maxDate = new Date();
  }

  private getCcyList(): void {
    this.store.select(selectCurrencyList).subscribe((currencyList) => {
      this.currencyList = [...currencyList].sort(
        (a, b) => this.ccySortOrder.indexOf(a) - this.ccySortOrder.indexOf(b),
      );
    });
  }

  private getCredebtorOverviewList(): void {
    const queryparams = {
      // originatorId: this.originatorId.toLocaleString(),
    };
    this.tradeTransactionsHttpService
      .getCredebtorOverviewList(queryparams)
      .subscribe({
        next: (response) => {
          const rowData =
            Array.isArray(response) && response.length
              ? response.filter(
                  ({ groupId }: SummaryByOriginatorResponseInterface) =>
                    groupId && groupId !== 'multiple',
                )
              : [];

          const creditorList = this.sortByName(
            this.formatList(rowData, 'creditor'),
          );
          const debtorList = this.sortByName(
            this.formatList(rowData, 'debtor'),
          );
          this.credebtors = [...debtorList, ...creditorList];
        },
      });
  }

  private getDebtorsList(): void {
    this.tradeTransactionsHttpService.getDebtorsList().subscribe({
      next: (response) => {
        this.debtors = [...response];
      },
    });
  }
  private formatList(rowData: any[], key: string) {
    return rowData
      .filter((item) => item[`${key}Name`])
      .map((item) => ({
        id: item[`${key}Id`],
        name: item[`${key}Name`],
        ccy: item.ccy,
        remainingValue: 0.0,
        vatIncluded: !!item.vatIncluded,
        faceValue: item.faceValue,
      }));
  }

  private sortByName(items: any[]) {
    return items.sort((a, b) => {
      const nameA = a.name?.trim().toLowerCase() || '';
      const nameB = b.name?.trim().toLowerCase() || '';
      return nameA?.localeCompare(nameB);
    });
  }

  customSearch(term: string, item: any): boolean {
    if (!term) {
      return true;
    }
    const searchTerm = term.toLowerCase();
    const nameMatches = item.name.toLowerCase().includes(searchTerm);
    const idMatches = item.id.toString().toLowerCase().includes(searchTerm);
    return nameMatches || idMatches;
  }

  updateTransactionType() {
    this.transactionUpdateForm
      .get('transactionType')
      ?.setValue(this.transactionType, { emitEvent: false });
  }

  onCreditorChange(debtor: { id: string }) {
    this.disableFileChange = !debtor?.id;
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.params = params;
    this.getReferenceData();
  }

  private getReferenceData(): void {
    this.gridApi.showLoadingOverlay();
    this.rowData = Reference_Data;
    this.updateSno();
  }

  onSubmit() {
    const fieldsToFormat = ['date'];
    this.isSubmitted = true;
    if (this.transactionUpdateForm.invalid) {
      return;
    }
    this.#spinnerService.setShowSpinner(true);
    let rawData = { ...this.transactionUpdateForm.value };
    rawData = getFormData(rawData);
    fieldsToFormat.forEach((field) => {
      const date = rawData[field];
      if (date) {
        rawData[field] =
          field === 'months'
            ? moment(date).format('MM')
            : moment(date).format('YYYY-MM-DD');
      }
    });

    this.doCreate(rawData);
  }

  doCreate(data: any): void {
    const payload = this.deserializeRawData(data);

    this.tradeTransactionsHttpService.addTransaction(payload).subscribe({
      next: (response: any) => {
        this.closeModal();
        this.transactionUpdateForm.reset();
        Swal.fire({
          icon: 'success',
          text: `${payload.type} added successfully`,
          showConfirmButton: false,
          timer: 5000,
        });
        this.#spinnerService.setShowSpinner(false);
      },
      error: (error: any) => {
        Swal.fire({
          icon: 'error',
          html: this.extractError(error.error.details || error.error.message),
          text: 'Error',
          showConfirmButton: true,
          confirmButtonText: 'Ok',
        });
        this.#spinnerService.setShowSpinner(false);
      },
    });
  }

  private deserializeRawData(model: any) {
    const dto = {
      type: this.transactionTypeToTypeMap.get(model.transactionType),
      debtorId: model.debtor,
      issuedDate: model.date,
      ccy: model.ccy,
      faceValue: Number(model.amount),
      file: model.file,
    };

    return dto;
  }

  private serialize(dto: any) {
    const model = {
      transactionType: this.getKeyByValue(
        this.transactionTypeToTypeMap,
        dto.type,
      ),
      debtor: dto.debtorId,
      date: dto.issuedDate,
      ccy: dto.ccy,
      amount: dto.faceValue,
      file: dto.file,
    };

    return model;
  }

  private getKeyByValue(
    map: Map<string, string | number>,
    val: string | number,
  ): string | number | null {
    for (const [key, value] of map) {
      if (value === val) {
        return key;
      }
    }

    return null;
  }

  private extractError(err: any): string {
    const messages: any = typeof err === 'string' ? err : extractErrorList(err);
    const paragraphs =
      typeof err === 'string'
        ? messages
        : messages.map((item: any) => `<p>${item}</p>`);
    return typeof err === 'string' ? paragraphs : paragraphs.join('\n');
  }

  updateSno() {
    const result = this.commonService.updateSno(this.rowData, 0, null);
    this.rowData = [...result];
  }

  closeModal() {
    this.activeModal.dismiss();
  }

  subscribeToTransactionChange(): void {
    const transactionChangeSubscription = this.typeCtrl?.valueChanges.subscribe(
      (value) => {
        if (!value) return;

        this.disableFileChange = false;
        this.fileCtrl?.setValue('');
        if (value == 'Bank Transmission Receipt') {
          this.pageTitle = 'Enter Transmission Receipt';
        } else if (value == 'Notice of Debtor Remittance') {
          this.pageTitle = 'Enter Debtor Remittance';
        } else if (value === 'Credit Note') {
          this.pageTitle = 'New Credit Note';
        } else if (value === 'Account Reconciliation') {
          this.closeModal();
          this.modalService.open(CashReconcilationComponent, {
            backdrop: true,
          });
          return;
        } else {
          this.transactionType = value;
        }
      },
    );

    if (transactionChangeSubscription) {
      this.subscriptions.add(transactionChangeSubscription);
    }
  }

  fileChange(event: Event): void {
    const maxSize = 10_000_000; // 10 MB
    const allowedFileType = 'application/pdf';
    const target = event.target as HTMLInputElement;
    let file = target.files?.[0] || null;
    if (!file) {
      file = null;
      target.value = '';
      return;
    }

    if (file.type !== allowedFileType) {
      target.value = '';
      Swal.fire({
        icon: 'error',
        text: 'Validation failed: Only PDF files are allowed.',
        showConfirmButton: true,
        timer: 5000,
      });
      return;
    }

    if (file.size > maxSize) {
      target.value = '';
      Swal.fire({
        icon: 'error',
        text: 'Validation failed: File size must be less than 10 MB.',
        showConfirmButton: true,
        timer: 5000,
      });

      return;
    }

    this.fileCtrl?.setValue(file);
  }

  clearFileInput(fileInputEl: HTMLInputElement): void {
    fileInputEl.files = null;
    fileInputEl.value = '';
    this.fileCtrl?.setValue(null);
    this.fileCtrl?.updateValueAndValidity();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
